import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Space,
  Row,
  Col,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { BookOutlined, SafetyCertificateOutlined, TeamOutlined, HomeOutlined, CarOutlined, NotificationOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { Guideline, GuidelineCategory, GuidelineStatus, GuidelineRecord } from '@/types/community/guideline';
import { addGuideline, updateGuideline } from '@/api/guidelines';
import { getAmenities } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import type { Amenity } from '@/types/resources/property';
import { getTemplatesByCategory } from '@/utils/guidelineTemplates';

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

interface GuidelineModalProps {
  open: boolean;
  guideline?: Guideline | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const CATEGORY_MAP: { [key in GuidelineCategory]: { name: string; description: string; icon: React.ReactNode } } = {
  general_conduct: {
    name: 'General Conduct',
    description: 'Basic rules for all residents and visitors.',
    icon: <TeamOutlined />,
  },
  amenity_usage: {
    name: 'Amenity Usage',
    description: 'Rules for using common facilities like pool, gym, clubhouse.',
    icon: <HomeOutlined />,
  },
  parking_vehicles: {
    name: 'Parking & Vehicles',
    description: 'Guidelines for vehicle parking and movement within the premises.',
    icon: <CarOutlined />,
  },
  safety_security: {
    name: 'Safety & Security',
    description: 'Important safety protocols and security measures.',
    icon: <SafetyCertificateOutlined />,
  },
  announcements_notifications: {
    name: 'Announcements & Notifications',
    description: 'How official communications are handled.',
    icon: <NotificationOutlined />,
  },
  common_expenses: {
    name: 'Common Expenses',
    description: 'Guidelines for shared expenses and billing.',
    icon: <BookOutlined />,
  },
  maintenance_requests: {
    name: 'Maintenance Requests',
    description: 'Procedures and priorities for maintenance requests.',
    icon: <BookOutlined />,
  },
};

const GUIDELINE_STATUSES: { value: GuidelineStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' }
];

export default function GuidelineModal({ open, guideline, onCancel }: GuidelineModalProps) {
  const { t } = useTranslation(['guidelines', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [amenityData, setAmenityData] = useState<{ id: string; name: string } | null>(null);
  const [authorData, setAuthorData] = useState<{ id: string; name: string } | null>(null);
  const [subPolicyContents, setSubPolicyContents] = useState<Record<string, string>>({});

  // Fetch amenities
  const { data: amenitiesData } = useQuery({
    queryKey: ['amenities', firestoreUser?.workspace?.id],
    queryFn: () => getAmenities({ firestoreUser: firestoreUser! }),
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000,
  });
  const amenities: Amenity[] = amenitiesData?.amenities ?? [];

  // Mutations
  const addGuidelineMutation = useMutation({
    mutationFn: (data: Omit<Guideline, 'id'>) => addGuideline(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('guidelines:addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['guidelines'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Add guideline error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to add guideline: ${errorMessage}`);
    }
  });

  const updateGuidelineMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<Guideline, 'id'>> }) => 
      updateGuideline(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('guidelines:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['guidelines'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Update guideline error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to update guideline: ${errorMessage}`);
    }
  });

  // Initialize form when guideline changes
  useEffect(() => {
    if (guideline) {
      // Load existing records into sub-policy contents
      const contents: Record<string, string> = {};
      guideline.records.forEach(record => {
        if (record.subPolicy) {
          contents[record.subPolicy] = record.content;
        }
      });
      setSubPolicyContents(contents);
      
      form.setFieldsValue({
        status: guideline.status,
        version: guideline.version,
        effectiveDate: guideline.effectiveDate ? dayjs(guideline.effectiveDate) : undefined,
        expiryDate: guideline.expiryDate ? dayjs(guideline.expiryDate) : undefined,
        authorId: guideline.author?.id
      });
      setAuthorData(guideline.author || null);
    } else {
      // Pre-fill with template defaults for all categories except amenity_usage
      const allTemplates = Object.keys(CATEGORY_MAP)
        .filter(cat => cat !== 'amenity_usage')
        .flatMap(cat => getTemplatesByCategory(cat as GuidelineCategory));
      const initialContents: Record<string, string> = {};
      allTemplates.forEach(t => { initialContents[t.subPolicy] = t.defaultContent; });
      setSubPolicyContents(initialContents);
      form.resetFields();
      setAmenityData(null);
      setAuthorData(null);
    }
  }, [guideline, form]);

  const handleSubPolicyChange = (subPolicy: string, value: string) => {
    setSubPolicyContents(prev => ({ ...prev, [subPolicy]: value }));
  };

  const handleSubmit = async (values: any) => {
    if (!firestoreUser) {
      message.error(t('common:notAuthenticated'));
      return;
    }

    // Additional client-side validation
    if (!values.status) {
      message.error('Guideline status is required');
      return;
    }
    if (!values.version?.trim()) {
      message.error('Guideline version is required');
      return;
    }
    if (!values.effectiveDate) {
      message.error('Guideline effective date is required');
      return;
    }

    // Check if at least one sub-policy has content
    const allTemplates = Object.keys(CATEGORY_MAP)
      .filter(cat => cat !== 'amenity_usage')
      .flatMap(cat => getTemplatesByCategory(cat as GuidelineCategory));
    const hasContent = allTemplates.some(t => subPolicyContents[t.subPolicy]?.trim()) ||
                      amenities.some(amenity => subPolicyContents[`amenity_${amenity.id}`]?.trim());
    if (!hasContent) {
      message.error('At least one policy section must have content');
      return;
    }

    try {
      const records: GuidelineRecord[] = [];
      
      // Add records for non-amenity templates
      allTemplates.forEach(template => {
        const content = subPolicyContents[template.subPolicy];
        if (content?.trim()) {
          const record: GuidelineRecord = {
            content: content.trim(),
            category: template.category,
            subPolicy: template.subPolicy,
            amenity: template.category === 'amenity_usage' ? amenityData || undefined : undefined
          };
          records.push(record);
        }
      });

      // Add records for amenities
      amenities.forEach(amenity => {
        const content = subPolicyContents[`amenity_${amenity.id}`];
        if (content?.trim()) {
          const record: GuidelineRecord = {
            content: content.trim(),
            category: 'amenity_usage',
            subPolicy: `amenity_${amenity.id}`,
            amenity: {
              id: amenity.id,
              name: amenity.name
            }
          };
          records.push(record);
        }
      });

      const guidelineData: Omit<Guideline, 'id'> = {
        records: records,
        status: values.status,
        version: values.version.trim(),
        effectiveDate: values.effectiveDate?.toDate() || new Date(),
        expiryDate: values.expiryDate?.toDate() || undefined,
        author: authorData ? { ...authorData, type: 'employee' } : undefined,
        workspace: {
          id: firestoreUser.workspace!.id,
          name: firestoreUser.workspace!.name,
          slug: firestoreUser.workspace!.slug
        }
      };

      if (guideline) {
        updateGuidelineMutation.mutate({ id: guideline.id, data: guidelineData });
      } else {
        addGuidelineMutation.mutate(guidelineData);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(`Form submission failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAmenityData(null);
    setAuthorData(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {guideline ? t('guidelines:editGuideline') : t('guidelines:addGuideline')}
        </Title>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'draft',
          effectiveDate: dayjs(),
          version: '1.0'
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('guidelines:status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('guidelines:selectStatus')}>
                {GUIDELINE_STATUSES.map(status => (
                  <Select.Option key={status.value} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="version"
              label={t('guidelines:version')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('guidelines:versionPlaceholder', 'e.g. 1.0')} />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="effectiveDate"
              label={t('guidelines:effectiveDate')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expiryDate"
              label={t('guidelines:expiryDate')}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item
          name="authorId"
          label={t('guidelines:author')}
        >
          <EmployeeSelector onEmployeeDataChange={setAuthorData} />
        </Form.Item>
        
        {/* Category sections */}
        {Object.entries(CATEGORY_MAP).map(([catKey, cat]) => {
          if (catKey === 'amenity_usage') {
            // Special handling for amenity usage - show all amenities
            return (
              <div key={catKey} style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 16 }}>
                  {cat.icon} {cat.name}
                </Title>
                {cat.description && (
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    {cat.description}
                  </Paragraph>
                )}
                {amenities.length > 0 ? (
                  amenities.map(amenity => (
                    <Form.Item
                      key={`amenity_${amenity.id}`}
                      label={`${amenity.name} Usage Guidelines`}
                      extra={`Guidelines for using the ${amenity.name} facility`}
                    >
                      <TextArea
                        rows={4}
                        value={subPolicyContents[`amenity_${amenity.id}`] || ''}
                        onChange={e => handleSubPolicyChange(`amenity_${amenity.id}`, e.target.value)}
                        placeholder={`Enter guidelines for ${amenity.name} usage...`}
                      />
                    </Form.Item>
                  ))
                ) : (
                  <Paragraph type="secondary">
                    No amenities found. Please add amenities first.
                  </Paragraph>
                )}
              </div>
            );
          } else {
            // Regular category handling
            const templates = getTemplatesByCategory(catKey as GuidelineCategory);
            return (
              <div key={catKey} style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 16 }}>
                  {cat.icon} {cat.name}
                </Title>
                {cat.description && (
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    {cat.description}
                  </Paragraph>
                )}
                {templates.map(template => (
                  <Form.Item
                    key={template.subPolicy}
                    label={template.title}
                    extra={template.description}
                  >
                    <TextArea
                      rows={4}
                      value={subPolicyContents[template.subPolicy]}
                      onChange={e => handleSubPolicyChange(template.subPolicy, e.target.value)}
                      placeholder={`Enter ${template.title.toLowerCase()} guidelines...`}
                    />
                  </Form.Item>
                ))}
              </div>
            );
          }
        })}
        
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>{t('common:actions.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={addGuidelineMutation.isPending || updateGuidelineMutation.isPending}>
              {guideline ? t('common:actions.update') : t('common:actions.add')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}